import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';

import Modele_1 from '../components/Models/Modele_1.vue';
import Modele_2 from '../components/Models/Modele_2.vue';
import Modele_3 from '../components/Models/Modele_3.vue';
import Modele_4 from '../components/Models/Modele_4.vue';
import Modele_5 from '../components/Models/Modele_5.vue';
import Modele_6 from '../components/Models/Modele_6.vue';
import Modele_7 from '../components/Models/Modele_7.vue';

const modeleComponents = {
  Modele_1,
  Modele_2,
  Modele_3,
  Modele_4,
  Modele_5,
  Modele_6,
  Modele_7,
};

const normalizeModelName = (model) => (
  (model || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
);

export function useGalleryTemplate(props) {
  const route = useRoute();
  const id = ref(props.exhibitId ?? route.params.id);
  const homePage = ref(props.homePageProps);
  const selectedTemplate = ref('');
  const exhibitName = ref('');
  const userName = ref('');
  const homeImage = ref({});
  const resources = ref([]);
  const resourceUrls = ref([]);
  const loading = ref(false);
  const error = ref(null);

  const dynamicComponent = computed(() => modeleComponents[selectedTemplate.value] || null);

  const exitHomePage = () => {
    homePage.value = false;
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 80);
  };

  const getResourceType = (resource) => {
    const name = resource?.name || '';
    if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name)) return 'image';
    if (/\.(mp4|mov|avi|webm|mkv)$/i.test(name)) return 'video';
    if (/\.(mp3|wav|ogg|m4a)$/i.test(name)) return 'audio';
    if (/\.(pdf)$/i.test(name)) return 'pdf';
    if (/\.(doc|docx)$/i.test(name)) return 'doc';
    return 'file';
  };

  const isImageResource = (resource) => getResourceType(resource) === 'image';

  const fetchData = async () => {
    try {
      loading.value = true;
      error.value = null;
      id.value = props.exhibitId ?? route.params.id;
      const exhibitParam = id.value;
      const baseUrl = process.env.VUE_APP_BASEURL;
      const request = async (promise, fallback) => {
        try {
          const response = await promise;
          return response.data;
        } catch (requestError) {
          error.value = error.value || requestError;
          return fallback;
        }
      };

      const [exhibitData, userData, homeData, modelData, allResources, galleryRows] = await Promise.all([
        request(axios.get(`${baseUrl}/exhibit/${exhibitParam}`), {}),
        request(axios.get(`${baseUrl}/user/${exhibitParam}`), {}),
        request(axios.get(`${baseUrl}/exhibit_home/${exhibitParam}`), []),
        request(axios.get(`${baseUrl}/model/${exhibitParam}`), []),
        request(axios.post(`${baseUrl}/resources`, { exhibitId: exhibitParam }), []),
        request(axios.get(`${baseUrl}/galleryResources/${exhibitParam}`), []),
      ]);

      exhibitName.value = exhibitData?.exhibit_name || '';
      userName.value = userData?.organisation || userData?.name || '';
      homeImage.value = homeData?.[0] || {};

      selectedTemplate.value = normalizeModelName(modelData?.[0]?.model);

      const galleryIds = galleryRows.map((row) => Number(row.resource_id));
      const filtered = galleryIds
        .map((resourceId) => allResources.find((resource) =>
          Number(resource.id) === resourceId || Number(resource.resource_id) === resourceId
        ))
        .filter(Boolean);

      resources.value = filtered;
      resourceUrls.value = Array(filtered.length).fill('');
      filtered.forEach(async (resource, index) => {
        try {
          const response = await axios.get(`${baseUrl}/s3Resource/${resource.name}`);
          const nextUrls = [...resourceUrls.value];
          nextUrls[index] = response.data;
          resourceUrls.value = nextUrls;
        } catch {
          const nextUrls = [...resourceUrls.value];
          nextUrls[index] = null;
          resourceUrls.value = nextUrls;
        }
      });
    } catch (fetchError) {
      error.value = fetchError;
    } finally {
      loading.value = false;
    }
  };

  onMounted(fetchData);

  return {
    dynamicComponent,
    error,
    exhibitName,
    exitHomePage,
    fetchData,
    getResourceType,
    homeImage,
    homePage,
    id,
    isImageResource,
    loading,
    resourceUrls,
    resources,
    selectedTemplate,
    userName,
  };
}
